import { arrowHead, arrowLength } from '../model/arrow';
import { cellIndex, countActiveCells, countOccupiedCells, type BoardData } from '../model/board';
import { DIRECTIONS, DIRECTION_VECTORS, type Direction } from '../model/direction';
import { buildDependencyGraph } from './dependencies';
import { computeWaves, solveWithTrace } from './solver';
import { CURRENT_DIFFICULTY_MODEL, clamp01 } from './difficultyModel';

export interface PuzzleAnalysis {
  readonly arrowCount: number;
  readonly activeCellCount: number;
  /** Cells covered by arrow paths; occupancy is measured in cells, not arrows. */
  readonly occupiedCellCount: number;
  readonly occupancyRatio: number;
  readonly meanArrowLength: number;
  readonly maxArrowLength: number;
  readonly turnCount: number;

  readonly initialLegalMoves: number;
  readonly minLegalMoves: number;
  readonly maxLegalMoves: number;
  readonly meanLegalMoves: number;
  readonly medianLegalMoves: number;

  readonly forcedSteps: number;
  readonly forcedStepRatio: number;
  readonly longestForcedSequence: number;

  readonly dependencyDepth: number;
  readonly dependencyEdgeCount: number;
  readonly rootCount: number;
  readonly meanBranching: number;

  readonly componentCount: number;
  readonly componentSizes: readonly number[];
  readonly largestComponentSize: number;

  readonly unlockEvents: number;
  readonly largestUnlockBurst: number;

  readonly directionDistribution: Readonly<Record<Direction, number>>;
  readonly directionalImbalance: number;
  readonly spatialDispersion: number;
  readonly repetitionPenalty: number;
  readonly silhouetteCoverage: number;

  readonly estimatedDifficulty: number;
  readonly solutionLength: number;
  readonly solvable: boolean;
}

/**
 * Computes every published metric in one pass over the canonical solve plus a handful of
 * linear board scans. Only exact IEEE-754 operations (+ - * /) are used, so results are
 * bit-identical across runtimes and can be compared in fixtures.
 */
export function analyzeBoard(board: BoardData): PuzzleAnalysis {
  const arrowCount = board.arrows.length;
  const activeCellCount = countActiveCells(board);
  const occupiedCellCount = countOccupiedCells(board);
  const trace = solveWithTrace(board);
  const graph = buildDependencyGraph(board);
  const waves = computeWaves(board);

  const legalCounts = trace.legalCounts;
  const initialLegalMoves = legalCounts.length > 0 ? legalCounts[0] : 0;

  let minLegalMoves = arrowCount > 0 ? Number.MAX_SAFE_INTEGER : 0;
  let maxLegalMoves = 0;
  let legalTotal = 0;
  let forcedSteps = 0;
  let longestForcedSequence = 0;
  let currentForcedRun = 0;

  for (const count of legalCounts) {
    if (count < minLegalMoves) minLegalMoves = count;
    if (count > maxLegalMoves) maxLegalMoves = count;
    legalTotal += count;
    if (count === 1) {
      forcedSteps += 1;
      currentForcedRun += 1;
      if (currentForcedRun > longestForcedSequence) longestForcedSequence = currentForcedRun;
    } else {
      currentForcedRun = 0;
    }
  }
  if (minLegalMoves === Number.MAX_SAFE_INTEGER) minLegalMoves = 0;

  let unlockEvents = 0;
  let largestUnlockBurst = 0;
  for (const count of trace.unlockCounts) {
    unlockEvents += count;
    if (count > largestUnlockBurst) largestUnlockBurst = count;
  }

  const directionDistribution = countDirections(board);
  const componentSizes = [...graph.componentSizes].sort((a, b) => b - a);

  let totalLength = 0;
  let maxArrowLength = 0;
  let turnCount = 0;
  for (const arrow of board.arrows) {
    const length = arrowLength(arrow);
    totalLength += length;
    if (length > maxArrowLength) maxArrowLength = length;
    turnCount += countTurns(arrow.cells);
  }

  const analysisBase = {
    arrowCount,
    activeCellCount,
    occupiedCellCount,
    occupancyRatio: activeCellCount === 0 ? 0 : occupiedCellCount / activeCellCount,
    meanArrowLength: arrowCount === 0 ? 0 : totalLength / arrowCount,
    maxArrowLength,
    turnCount,

    initialLegalMoves,
    minLegalMoves,
    maxLegalMoves,
    meanLegalMoves: legalCounts.length === 0 ? 0 : legalTotal / legalCounts.length,
    medianLegalMoves: median(legalCounts),

    forcedSteps,
    forcedStepRatio: legalCounts.length === 0 ? 0 : forcedSteps / legalCounts.length,
    longestForcedSequence,

    dependencyDepth: waves.length,
    dependencyEdgeCount: graph.edgeCount,
    rootCount: graph.roots.length,
    meanBranching: arrowCount === 0 ? 0 : graph.edgeCount / arrowCount,

    componentCount: graph.componentSizes.length,
    componentSizes,
    largestComponentSize: componentSizes.length > 0 ? componentSizes[0] : 0,

    unlockEvents,
    largestUnlockBurst,

    directionDistribution,
    directionalImbalance: directionalImbalance(directionDistribution, arrowCount),
    spatialDispersion: spatialDispersion(board),
    repetitionPenalty: repetitionPenalty(board),
    silhouetteCoverage: activeCellCount === 0 ? 0 : silhouetteCoverage(board, activeCellCount),

    solutionLength: trace.solution.length,
    solvable: trace.solved,
  };

  return { ...analysisBase, estimatedDifficulty: estimateDifficulty(analysisBase) };
}

type AnalysisWithoutDifficulty = Omit<PuzzleAnalysis, 'estimatedDifficulty'>;

export function estimateDifficulty(analysis: AnalysisWithoutDifficulty): number {
  const { weights, scales } = CURRENT_DIFFICULTY_MODEL;
  // Board weight is about how much there is to unpick, which for winding arrows is better
  // captured by covered cells than by arrow count alone.
  const arrowFeature = clamp01(analysis.occupiedCellCount / scales.occupiedCells);
  const depthFeature = clamp01(analysis.dependencyDepth / scales.dependencyDepth);
  const forcedFeature = clamp01(analysis.forcedStepRatio);
  // Openness is measured as a share of the board: within a row only the outermost arrow
  // per direction can be legal, so absolute counts grow with size and say little on their own.
  const arrowCount = Math.max(1, analysis.arrowCount);
  const openingFeature = 1 - clamp01(analysis.initialLegalMoves / arrowCount / scales.openRatio);
  const meanFeature = 1 - clamp01(analysis.meanLegalMoves / arrowCount / scales.meanRatio);
  const occupancyFeature = clamp01(analysis.occupancyRatio);
  const edgeFeature = clamp01(analysis.meanBranching / scales.edgesPerArrow);

  const raw =
    weights.boardWeight * arrowFeature +
    weights.dependencyDepth * depthFeature +
    weights.forcedStepRatio * forcedFeature +
    weights.closedOpening * openingFeature +
    weights.closedAverage * meanFeature +
    weights.occupancy * occupancyFeature +
    weights.edgeDensity * edgeFeature;

  return clamp01(raw);
}

function countDirections(board: BoardData): Record<Direction, number> {
  const counts: Record<Direction, number> = { up: 0, right: 0, down: 0, left: 0 };
  for (const arrow of board.arrows) counts[arrow.direction] += 1;
  return counts;
}

/** Corners along a path; the visual signature that separates winding arrows from straight ones. */
function countTurns(cells: readonly { x: number; y: number }[]): number {
  let turns = 0;
  for (let i = 2; i < cells.length; i += 1) {
    const dx1 = cells[i - 1].x - cells[i - 2].x;
    const dy1 = cells[i - 1].y - cells[i - 2].y;
    const dx2 = cells[i].x - cells[i - 1].x;
    const dy2 = cells[i].y - cells[i - 1].y;
    if (dx1 !== dx2 || dy1 !== dy2) turns += 1;
  }
  return turns;
}

/** 0 when all four directions are equally represented, 1 when a single direction is used. */
function directionalImbalance(counts: Record<Direction, number>, total: number): number {
  if (total === 0) return 0;
  const expected = total / 4;
  let deviation = 0;
  for (const direction of DIRECTIONS) {
    const diff = counts[direction] - expected;
    deviation += diff < 0 ? -diff : diff;
  }
  // Maximum total deviation is 2 * total * (3/4).
  return clamp01(deviation / (1.5 * total));
}

/** Normalised mean Manhattan distance of occupied cells from their centroid. */
function spatialDispersion(board: BoardData): number {
  let count = 0;
  let sumX = 0;
  let sumY = 0;
  for (const arrow of board.arrows) {
    for (const cell of arrow.cells) {
      sumX += cell.x;
      sumY += cell.y;
      count += 1;
    }
  }
  if (count === 0) return 0;
  const centroidX = sumX / count;
  const centroidY = sumY / count;

  let distance = 0;
  for (const arrow of board.arrows) {
    for (const cell of arrow.cells) {
      const dx = cell.x - centroidX;
      const dy = cell.y - centroidY;
      distance += (dx < 0 ? -dx : dx) + (dy < 0 ? -dy : dy);
    }
  }
  const normaliser = (board.width + board.height) / 2;
  return normaliser === 0 ? 0 : clamp01(distance / count / normaliser);
}

/** Fraction of arrows whose head sits beside another head pointing the same way. */
function repetitionPenalty(board: BoardData): number {
  const count = board.arrows.length;
  if (count === 0) return 0;
  const headDirections = new Map<number, Direction>();
  for (const arrow of board.arrows) {
    const head = arrowHead(arrow);
    headDirections.set(cellIndex(board, head.x, head.y), arrow.direction);
  }

  let repeats = 0;
  for (const arrow of board.arrows) {
    const head = arrowHead(arrow);
    for (const direction of DIRECTIONS) {
      const { dx, dy } = DIRECTION_VECTORS[direction];
      const neighbour = headDirections.get(cellIndex(board, head.x + dx, head.y + dy));
      if (neighbour === arrow.direction) {
        repeats += 1;
        break;
      }
    }
  }
  return clamp01(repeats / count);
}

/**
 * How evenly arrows are spread over the silhouette: 1 when every 2x2 block of active cells
 * contains at least one arrow. Low values mean the silhouette reads as empty space.
 */
function silhouetteCoverage(board: BoardData, activeCellCount: number): number {
  const blockWidth = Math.ceil(board.width / 2);
  const blocks = new Uint8Array(blockWidth * Math.ceil(board.height / 2));
  const activeBlocks = new Uint8Array(blocks.length);

  for (let y = 0; y < board.height; y += 1) {
    for (let x = 0; x < board.width; x += 1) {
      if (board.activeMask[y * board.width + x] === 1) {
        activeBlocks[(y >> 1) * blockWidth + (x >> 1)] = 1;
      }
    }
  }
  for (const arrow of board.arrows) {
    for (const cell of arrow.cells) {
      blocks[(cell.y >> 1) * blockWidth + (cell.x >> 1)] = 1;
    }
  }

  let activeBlockCount = 0;
  let coveredBlockCount = 0;
  for (let i = 0; i < blocks.length; i += 1) {
    if (activeBlocks[i] === 1) {
      activeBlockCount += 1;
      if (blocks[i] === 1) coveredBlockCount += 1;
    }
  }
  if (activeBlockCount === 0 || activeCellCount === 0) return 0;
  return coveredBlockCount / activeBlockCount;
}

function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = sorted.length >> 1;
  if ((sorted.length & 1) === 1) return sorted[middle];
  return (sorted[middle - 1] + sorted[middle]) / 2;
}
